import React, { useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileImport,
  faXmark,
  faTriangleExclamation,
  faCheckCircle,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../../components/ui";
import { apiFetch } from "../../../services/apiClient";

/** Mirrors the Go AssetExportPayload struct. */
interface AssetExportPayload {
  version: string;
  exportedAt: string;
  exportedBy: string;
  assets: AssetExportItem[];
  warnings?: string[];
}

interface AssetExportItem {
  originalId: number;
  name: string;
  type: string;
  category: string;
  description: string;
  tags: string[];
  imageUrl?: string | null;
  sections: {
    originalId: number;
    name: string;
    description: string;
    sectionOrder: number;
  }[];
  children?: AssetExportItem[];
}

/** Mirrors the Go AssetImportResponse struct. */
interface AssetImportResponse {
  importedCount: number;
  rootAssetIds: number[];
  warnings: string[];
}

export interface AssetImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful import with the new root asset IDs. */
  onImported: (_rootIds: number[]) => void;
}

type Phase = "pick" | "validate" | "done";

/** Count total assets (including children) in an export payload. */
function countAssets(items: AssetExportItem[]): number {
  let n = 0;
  function walk(list: AssetExportItem[]) {
    for (const item of list) {
      n++;
      if (item.children) walk(item.children);
    }
  }
  walk(items);
  return n;
}

const AssetImportModal: React.FC<AssetImportModalProps> = ({ isOpen, onClose, onImported }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [phase, setPhase] = useState<Phase>("pick");
  const [fileName, setFileName] = useState<string>("");
  const [payload, setPayload] = useState<AssetExportPayload | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<AssetImportResponse | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const reset = () => {
    setPhase("pick");
    setFileName("");
    setPayload(null);
    setParseError(null);
    setValidationWarnings([]);
    setImporting(false);
    setImportResult(null);
    setImportError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // ── File selection ──────────────────────────────────────────────────────────
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParseError(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed: AssetExportPayload = JSON.parse(ev.target?.result as string);
        if (!parsed.version || !Array.isArray(parsed.assets)) {
          throw new Error("Invalid export file format — missing required fields.");
        }
        setPayload(parsed);

        // Surface any warnings that were embedded during export.
        const exportWarnings = parsed.warnings ?? [];
        setValidationWarnings(exportWarnings);
        setPhase("validate");
      } catch (err) {
        setParseError(err instanceof Error ? err.message : "Failed to parse file.");
        setPayload(null);
      }
    };
    reader.readAsText(file);
  };

  // ── Import ──────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    if (!payload) return;
    setImporting(true);
    setImportError(null);
    try {
      const res = await apiFetch("/api/protected/assets/import", {
        method: "POST",
        body: JSON.stringify({ payload }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      const result: AssetImportResponse = await res.json();
      setImportResult(result);
      setPhase("done");
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  // ── Done ────────────────────────────────────────────────────────────────────
  const handleDone = () => {
    if (importResult) {
      onImported(importResult.rootAssetIds);
    }
    handleClose();
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  const assetCount = payload ? countAssets(payload.assets) : 0;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalHeader onClose={handleClose}>
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 bg-amber-400/15 border border-amber-400/30 rounded flex items-center justify-center flex-shrink-0">
            <FontAwesomeIcon icon={faFileImport} className="text-amber-400 text-xs" />
          </div>
          <h2 className="font-semibold text-base">Import Assets</h2>
        </div>
      </ModalHeader>

      <ModalBody>
        {/* ── Phase: pick ── */}
        {phase === "pick" && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">
              Select a <code className="text-amber-400">.json</code> file that was previously
              exported from this platform. The assets inside will be added to your library.
            </p>

            {parseError && (
              <div className="flex items-start gap-2 px-3 py-2 bg-red-900/30 border border-red-600/40 rounded text-red-300 text-xs">
                <FontAwesomeIcon icon={faXmark} className="flex-shrink-0 mt-0.5" />
                {parseError}
              </div>
            )}

            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Export file</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                onChange={handleFileChange}
                className="block w-full text-sm text-zinc-300 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-amber-400/10 file:text-amber-400 hover:file:bg-amber-400/20 file:cursor-pointer cursor-pointer bg-zinc-800 border border-zinc-700 rounded px-3 py-2"
              />
            </div>
          </div>
        )}

        {/* ── Phase: validate (preview + warnings) ── */}
        {phase === "validate" && payload && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="px-4 py-3 bg-zinc-800 border border-zinc-700 rounded space-y-1">
              <p className="text-xs text-zinc-500 uppercase tracking-widest mb-2">Import summary</p>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">File</span>
                <span className="text-zinc-200 font-medium">{fileName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Assets to import</span>
                <span className="text-zinc-200 font-medium">{assetCount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Exported by</span>
                <span className="text-zinc-200 font-medium">{payload.exportedBy || "—"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Format version</span>
                <span className="text-zinc-200 font-medium">{payload.version}</span>
              </div>
            </div>

            {/* Warnings */}
            {validationWarnings.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-amber-400 font-semibold uppercase tracking-widest">
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  {validationWarnings.length} reference warning
                  {validationWarnings.length !== 1 ? "s" : ""}
                </div>
                <div className="bg-amber-900/20 border border-amber-600/30 rounded px-3 py-2 space-y-1 max-h-40 overflow-y-auto">
                  {validationWarnings.map((w, i) => (
                    <p key={i} className="text-xs text-amber-300/80 leading-relaxed">
                      {w}
                    </p>
                  ))}
                </div>
                <p className="text-xs text-zinc-500">
                  These references may not resolve correctly in your account. You can still import —
                  the assets will be created but the broken links will remain until you fix them
                  manually.
                </p>
              </div>
            )}

            {validationWarnings.length === 0 && (
              <div className="flex items-center gap-2 px-3 py-2 bg-green-900/20 border border-green-600/30 rounded text-green-400 text-xs">
                <FontAwesomeIcon icon={faCheckCircle} />
                All references in this package are self-contained. Ready to import.
              </div>
            )}

            {importError && (
              <div className="flex items-start gap-2 px-3 py-2 bg-red-900/30 border border-red-600/40 rounded text-red-300 text-xs">
                <FontAwesomeIcon icon={faXmark} className="flex-shrink-0 mt-0.5" />
                {importError}
              </div>
            )}
          </div>
        )}

        {/* ── Phase: done ── */}
        {phase === "done" && importResult && (
          <div className="space-y-4">
            <div className="flex flex-col items-center text-center py-4 gap-3">
              <div className="w-14 h-14 bg-green-500/15 border border-green-500/30 rounded-2xl flex items-center justify-center">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-400 text-2xl" />
              </div>
              <div>
                <p className="font-semibold text-white">Import complete!</p>
                <p className="text-sm text-zinc-400 mt-1">
                  {importResult.importedCount} asset
                  {importResult.importedCount !== 1 ? "s" : ""} added to your library.
                </p>
              </div>
            </div>

            {importResult.warnings && importResult.warnings.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-amber-400 font-semibold uppercase tracking-widest">
                  <FontAwesomeIcon icon={faCircleInfo} />
                  {importResult.warnings.length} warning
                  {importResult.warnings.length !== 1 ? "s" : ""}
                </div>
                <div className="bg-amber-900/20 border border-amber-600/30 rounded px-3 py-2 space-y-1 max-h-32 overflow-y-auto">
                  {importResult.warnings.map((w, i) => (
                    <p key={i} className="text-xs text-amber-300/80 leading-relaxed">
                      {w}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <span />
        <div className="flex gap-3">
          {phase !== "done" && (
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
          )}

          {phase === "validate" && (
            <button
              type="button"
              onClick={() => {
                reset();
              }}
              className="px-4 py-2 text-sm text-zinc-400 hover:text-white border border-zinc-700 rounded transition-colors"
            >
              Back
            </button>
          )}

          {phase === "validate" && (
            <button
              type="button"
              onClick={handleImport}
              disabled={importing}
              className="px-4 py-2 bg-amber-400 text-zinc-950 text-sm font-bold rounded hover:bg-amber-300 transition-colors disabled:opacity-50"
            >
              {importing ? "Importing…" : "Import"}
            </button>
          )}

          {phase === "done" && (
            <button
              type="button"
              onClick={handleDone}
              className="px-4 py-2 bg-amber-400 text-zinc-950 text-sm font-bold rounded hover:bg-amber-300 transition-colors"
            >
              View Assets
            </button>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default AssetImportModal;
